import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import get from 'lodash/get';
import numbro from 'numbro';
import { combineLatest, Observable, ReplaySubject, Subject, BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, filter, first, map, pairwise, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';

import { CoreApiService } from './core-api.service';
import { NotificationService } from './notification.service';
import { ProfileService } from './profile/profile.service';
import { ProjectsService } from './projects/projects.service';
import { VisualQuery } from './search/advanced-query/visual-query.utils';
import { isSearchPath } from './search/search.utils';
import { SessionService } from './sessions/session.service';
import { ContainerType } from './shared/enums/container-type.enum';
import { PermissionAction } from './shared/enums/permission-action.enum';
import { RequestState, isInitialized, isLoading } from './shared/enums/request-state.enum';
import { ContainerReference } from './shared/models/container.model';
import { SavedSearch } from './shared/models/search/saved-search.model';
import { SearchFacets } from './shared/models/search/search-facets.model';
import { SearchQuery, SearchReturnType, SearchFilter } from './shared/models/search/search-query.model';
import { SearchResult, SearchResultResponse } from './shared/models/search/search-results.model';

interface StateFragment {
  name: string
  params: { projectId?: string }
}

interface StateChange {
  prev: StateFragment
  next: StateFragment
}

export enum SearchMode {
  Basic = 'basic',
  Advanced = 'advanced',
}

interface SavedSearchInput {
  label: string
  search: Partial<SearchQuery>
}

interface ResultExtras {
  elasticId?: string
  viewable?: boolean
  parent?: ContainerReference
}

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  readonly link = {
    active: 'dashboard',
  }
  readonly state$ = new BehaviorSubject<'default' | 'search'>('default')
  readonly stateChange$ = new BehaviorSubject<StateChange>(null)

  readonly search = {
    allData: false, // manage mode toggle
    acqId$: new BehaviorSubject(''),
    analysisLabel$: new BehaviorSubject(''),
    context: SearchReturnType.Session,
    facets: {} as SearchFacets, // available facets for the query
    query: '', // basic query text
    pageSize: 100,
    project: null,
    response: null as SearchResultResponse,
    results$: new BehaviorSubject<SearchResult[]>([]),
    resultType$: new BehaviorSubject(SearchReturnType.Session),
    selectedContainer$: new BehaviorSubject<{ type: ContainerType.Session | ContainerType.Subject, id: string }>(null),
    sessionTimestamp$: new Subject<string>(),
    searchStart$: new Subject<void>(),
    sidebarFilters: [] as SearchFilter[],
    structuredQuery: '',
    tableFilters: [] as SearchFilter[],
    visualQuery: null as VisualQuery,
    resetSearchState: null,
    enterSearchState: null,
    clearSessionTimestampFilters: null,
    searchMode: 'basic' as SearchMode,
  }

  readonly bidsViewActive$: BehaviorSubject<boolean> = new BehaviorSubject(false)
  readonly projectContext$: BehaviorSubject<string> = new BehaviorSubject(null) // route project context before entering search state

  private error = new Subject<Error>()
  readonly error$ = this.error.asObservable()

  private savedSearches = {
    state: RequestState.Empty,
    subject: new ReplaySubject<SavedSearch[]>(1),
  }
  readonly savedSearches$ = this.savedSearches.subject.asObservable()

  private signals = {
    cancelSearch$: new Subject<void>(),
  }

  private subscriptions = {
    location: Subscription.EMPTY,
    siteAdmin: Subscription.EMPTY,
  }

  // Custom filters that sync with the URL
  updatableFilters = [
    'file.name',
    'file.parent_ref.id',
    'acquisition.uid',
    'session.uid',
    'subject.uid',
  ];

  constructor(
    private coreApi: CoreApiService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private sessionService: SessionService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
    this.subscriptions.location = this.router.events?.pipe(
      filter((event) => {
        if (event instanceof NavigationStart) {
          const isToSearchState = isSearchPath(event.url);
          const parsedQuery = this.extractFlyqlParamFromUrl(event.url);
          if (parsedQuery || !isToSearchState) {
            this.search.structuredQuery = parsedQuery;
          }
        }

        return event instanceof NavigationEnd;
      }),
      debounceTime(50),
      map((event: NavigationEnd) => ({ name: event.url, params: this.activatedRoute.snapshot.queryParams })),
      pairwise(),
    ).subscribe(([prev, next]) => {
      this.stateChange$.next({ prev, next });
      this.updateState(prev, next);
    });
    this.subscriptions.siteAdmin = this.profileService.exhaustiveMode$.subscribe(exhaustiveMode => {
      this.search.allData = exhaustiveMode;
    });
  }

  get state(): string {
    return this.state$.value;
  }

  private updateState(prev?: StateFragment, next?: StateFragment): void {
    const isToSearchState = (!isSearchPath(prev?.name) && isSearchPath(next?.name))
    const isFromSearchState = (isSearchPath(prev?.name) && !isSearchPath(next?.name))
    if (isToSearchState) {
      this.state$.next('search');
    } else if (isFromSearchState) {
      this.resetSearchState();
    }
  }

  removeFiltersByKey(filterKey: string, filters: SearchFilter[]): SearchFilter[] {
    const searchFilters = [];
    for (let i = 0, len = filters.length; i < len; i++) {
      const filter = filters[i];
      for (const k in filter) {
        if (Object.keys(filter[k]).includes(filterKey)) {
          continue;
        }
        searchFilters.push(filter);
      }
    }
    return searchFilters;
  }

  buildSearchQuery(): SearchQuery {
    let filters = [...this.search.sidebarFilters, ...this.search.tableFilters];
    const project = this.search.project || {};
    if (project._id) {
      filters = this.removeFiltersByKey('project.label', filters); // remove fuzzy label filter
      filters.push({
        term: { 'project._id': project._id },
      });
    }
    const payload: SearchQuery = {
      filters: filters,
      return_type: this.search.context,
      all_data: this.search.allData || false,
    };
    if (this.search.structuredQuery) {
      payload.structured_query = this.search.structuredQuery;
    } else {
      payload.search_string = this.search.query;
    }
    return payload;
  }

  formatCountAsEstimated(facetCount: number): string {
    if (facetCount >= 1000) {
      return numbro(facetCount).format({ average: true, mantissa: 1, trimMantissa: true }) || '';
    }
    return facetCount.toString();
  }
  performSearch(resultCount = this.search.pageSize): Observable<SearchResultResponse> {
    this.signals.cancelSearch$.next();
    this.sessionService.clearCheckedSessions();
    this.search.searchStart$.next();
    const payload = this.buildSearchQuery();
    const params = {
      facets: true,
      size: Math.min(resultCount, 10000), // 10K is the limit on the API
    };
    const observable = combineLatest([
      this.coreApi.dataexplorer.search(params, payload),
      this.projectsService.projectPermissions$,
    ]).pipe(
      takeUntil(this.signals.cancelSearch$),
      take(1), // avoid EmptyError from first() if search is cancelled
      map(([response, projectPermissions]) => {
        response.results.forEach((responseItem: SearchResult & ResultExtras) => {
          if (responseItem._source) {
            const projectId = get(responseItem._source, 'project._id');
            const permissions = projectPermissions[projectId] || [];
            const viewable = permissions.includes(PermissionAction.ContainersViewMetadata);
            responseItem.viewable = viewable;
            responseItem._source.viewable = viewable;
            if (this.search.context === 'subject') {
              if (responseItem._source.subject) {
                responseItem._id = responseItem._source.subject._id;
              }
            } else if (this.search.context === 'session') {
              if (responseItem._source.session) {
                responseItem.elasticId = responseItem._id;
                responseItem._id = responseItem._source.session._id;
              }
            } else if (this.search.context === 'analysis') {
              responseItem.viewable = permissions.includes(PermissionAction.AnalysesViewMetadata);
            } else if (this.search.context === 'file') {
              if (responseItem._source.file) {
                responseItem._source.file.file_id = responseItem._id;
              }
              responseItem.viewable = permissions.includes(PermissionAction.FilesViewMetadata);
              responseItem.parent = {
                _id: responseItem._source.parent._id,
                container_type: responseItem._source.parent.type as ContainerType,
                permissions: responseItem._source.permissions,
                project: projectId,
              };
            }
          }
        });
        this.search.response = response;
        this.search.resultType$.next(this.search.context);
        this.search.results$.next(response.results);
        return response;
      }),
      shareReplay({ bufferSize: 1, refCount: true }), // only one request please
    );
    observable.subscribe(); // make it HOT
    return observable;
  }

  resetSearchState(): void {
    this.search.response = null;
    this.search.project = null;
    this.search.visualQuery = null;
    this.search.structuredQuery = '';
    this.search.acqId$.next('');
    this.search.analysisLabel$.next('');
    this.search.selectedContainer$.next({ type: null, id: null });
    this.state$.next('default');
  }

  getSavedSearches({ refresh = false } = {}): Observable<SavedSearch[]> {
    if (isLoading(this.savedSearches.state)) {
      return this.savedSearches$;
    }
    if (!isInitialized(this.savedSearches.state) || refresh) {
      this.savedSearches.state = RequestState.Fetching;
      this.coreApi.dataexplorer.queries.query().subscribe(data => {
        this.savedSearches.subject.next(data.slice().reverse());
      }, error => {
        this.error.next(error);
      }, () => {
        this.savedSearches.state = RequestState.Loaded;
      });
    }
    return this.savedSearches$;
  }

  saveSearch(input: SavedSearchInput): Observable<SavedSearch> {
    const observable = this.profileService.getProfile().pipe(
      first(data => Boolean(data)),
      switchMap(profile => {
        const query = this.buildSearchQuery();
        const search: Partial<SavedSearch> = {
          parent: {
            type: 'user',
            id: profile._id,
          },
          ...input,
          search: {
            filters: query.filters,
            return_type: query.return_type,
            all_data: query.all_data,
            ...input.search,
          },
        };
        return this.coreApi.dataexplorer.queries.post(search);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    observable.subscribe(() => {
      this.getSavedSearches({ refresh: true });
    });
    return observable;
  }

  private extractFlyqlParamFromUrl(url: string): string {
    const queryString = url.split('?')[1];
    if (!queryString) {
      return '';
    }

    const params = new URLSearchParams(queryString);
    const flyql = params.get('flyql');
    return flyql ?? '';
  }
}
